import React, { useState, useEffect } from "react";
import { useSearchParams, useLocation, useNavigate, Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { SEO } from "@/data/seo";
import { SITE } from "@/data/config";
import { getServiceBySlug } from "@/data/services";
import { Overline, PrimaryButton, SecondaryButton, Crumbs } from "@/components/Primitives";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { submitContact } from "@/lib/api";
import CTABlock from "@/components/CTABlock";

// Six help-needed buckets — kept short on purpose so the picker does not become
// its own decision tree. Preselection is delivered via React Router link state
// (or sessionStorage) — never via crawlable query parameters. Legacy ?service=
// URLs are still accepted for old bookmarks and then stripped from the URL.
const HELP_OPTIONS = [
  "Brand",
  "Website",
  "Marketing (local, social, content)",
  "Ongoing partner support",
  "Strategy call before deciding",
  "Not sure yet",
];

const readSessionPreselect = () => {
  try {
    const raw = sessionStorage.getItem("kostaja:contactPreselect");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_e) {
    return null;
  }
};

const FieldLabel = ({ children, required }) => (
  <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold mb-2 text-white/70">
    {children}
    {required ? " *" : ""}
  </label>
);

const Contact = () => {
  useSeo(SEO.contact);
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Priority order: link state > sessionStorage > legacy query params.
  const stateFromNav = location.state || null;
  const sessionState = stateFromNav ? null : readSessionPreselect();
  const src = stateFromNav || sessionState || {};
  const serviceParam = src.serviceSlug || params.get("service") || "";
  const intent = src.intent || params.get("intent") || "";
  const source = src.source || params.get("source") || "";
  const preselected = serviceParam ? getServiceBySlug(serviceParam) : null;

  const [form, setForm] = useState({
    name: "",
    email: "",
    businessName: "",
    helpNeeded: preselected ? preselected.name : "",
    message: preselected
      ? `I am interested in ${preselected.name}${intent === "quote" ? " (quote request)" : ""}.\n\n`
      : "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});

  // If we arrived with legacy ?service=/?intent= query params, normalize the
  // URL to the clean /contact path after mount so Google never sees the
  // parameter URL as a live in-app anchor. The form is already prefilled.
  useEffect(() => {
    if (params.get("service") || params.get("intent") || params.get("source")) {
      navigate("/contact", { replace: true, state: null });
    }
    // Clear one-shot session preselect so a later visit does not re-fill.
    try { sessionStorage.removeItem("kostaja:contactPreselect"); } catch (_e) { /* no-op */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (preselected && !form.helpNeeded) {
      setForm((f) => ({ ...f, helpNeeded: preselected.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceParam]);

  const update = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!form.email.trim()) errs.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Invalid email";
    if (!form.message.trim()) errs.message = "Required";
    if (!form.consent) errs.consent = "Required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResult(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        serviceSlug: serviceParam || null,
        source: source || (intent ? `intent:${intent}` : null),
      };
      const res = await submitContact(payload);
      setResult({ ok: true, message: res.message });
      setForm((f) => ({
        ...f,
        name: "",
        businessName: "",
        message: "",
        consent: false,
      }));
    } catch (err) {
      setResult({
        ok: false,
        message:
          err?.response?.data?.detail ||
          "Something went wrong submitting your request. Please try again or email info@kostaja.ca.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-black text-white">
      {/* HERO */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-12 lg:py-20">
          <Crumbs items={[{ to: "/", label: "Home" }, { label: "Contact" }]} />
          <div className="mt-8 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] font-black text-[#FFC600]">
            <span className="block w-8 h-px bg-[#FFC600]" aria-hidden />
            <span>Start a Project</span>
          </div>
          <h1 className="mt-6 text-5xl sm:text-6xl lg:text-[96px] font-black tracking-[-0.03em] leading-[0.9]">
            Tell us <span className="k-underline-yellow">what to fix.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-white/85 text-lg lg:text-2xl leading-[1.35] font-medium">
            You do not need to know the exact service. Kostaja will name the honest starting point.
          </p>
          <ul className="mt-8 max-w-2xl grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm text-white/70">
            <li className="flex items-start gap-2"><span className="mt-2 inline-block w-1.5 h-1.5 bg-[#FFC600] shrink-0" /><span>No pressure to commit on the first call.</span></li>
            <li className="flex items-start gap-2"><span className="mt-2 inline-block w-1.5 h-1.5 bg-[#FFC600] shrink-0" /><span>Scope is confirmed before larger work begins.</span></li>
            <li className="flex items-start gap-2"><span className="mt-2 inline-block w-1.5 h-1.5 bg-[#FFC600] shrink-0" /><span>Pricing is tied to the work and the outcome.</span></li>
            <li className="flex items-start gap-2"><span className="mt-2 inline-block w-1.5 h-1.5 bg-[#FFC600] shrink-0" /><span>If there is a simpler starting point, Kostaja will say so.</span></li>
          </ul>
          {preselected ? (
            <div className="mt-6 inline-block border-2 border-[#FFC600] px-4 py-2 text-sm">
              Inquiring about <span className="font-bold k-yellow">{preselected.name}</span>
              {intent === "quote" ? " (quote)" : ""}
            </div>
          ) : null}
        </div>
      </section>

      {/* FORM + ASIDE */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            {result ? (
              <div
                data-testid={result.ok ? "contact-success" : "contact-error"}
                className={`p-6 lg:p-8 border-2 ${
                  result.ok ? "bg-[#FFC600] border-black" : "bg-black text-white border-[#FFC600]"
                }`}
              >
                <Overline color={result.ok ? "black" : "yellow"} className="mb-2">
                  {result.ok ? "Message received" : "There was a problem"}
                </Overline>
                <p className={`text-lg font-semibold ${result.ok ? "text-black" : "text-white"}`}>
                  {result.message}
                </p>
                <div className="mt-5 flex gap-3 flex-wrap">
                  <SecondaryButton to="/services" testId="contact-after-services">
                    Browse services
                  </SecondaryButton>
                  <Link
                    to="/waitlist"
                    data-testid="contact-after-apply"
                    className="inline-flex items-center justify-center font-semibold px-7 py-4 border border-white/15 bg-black text-[#FFC600] hover:bg-[#FFC600] hover:text-black transition-colors text-sm tracking-wide"
                  >
                    Apply for a 2027 project
                  </Link>
                </div>
              </div>
            ) : null}

            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              noValidate
              className={`grid grid-cols-1 sm:grid-cols-2 gap-5 ${result?.ok ? "mt-10" : ""}`}
            >
              <div>
                <FieldLabel required>Your name</FieldLabel>
                <Input
                  data-testid="contact-name"
                  value={form.name}
                  onChange={update("name")}
                  className="rounded-none border border-white/15 h-12"
                />
                {errors.name && <div className="text-xs mt-1 font-semibold">{errors.name}</div>}
              </div>
              <div>
                <FieldLabel required>Email</FieldLabel>
                <Input
                  data-testid="contact-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="rounded-none border border-white/15 h-12"
                />
                {errors.email && <div className="text-xs mt-1 font-semibold">{errors.email}</div>}
              </div>
              <div>
                <FieldLabel>Business name</FieldLabel>
                <Input
                  data-testid="contact-business"
                  value={form.businessName}
                  onChange={update("businessName")}
                  className="rounded-none border border-white/15 h-12"
                />
              </div>
              <div>
                <FieldLabel>What are you trying to fix</FieldLabel>
                <select
                  data-testid="contact-help"
                  value={form.helpNeeded}
                  onChange={update("helpNeeded")}
                  className="w-full h-12 border border-white/15 px-3 bg-black"
                >
                  <option value="">Not sure yet</option>
                  {HELP_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                  {preselected && !HELP_OPTIONS.includes(preselected.name) ? (
                    <option value={preselected.name}>{preselected.name}</option>
                  ) : null}
                </select>
              </div>
              <div className="sm:col-span-2">
                <FieldLabel required>Message</FieldLabel>
                <Textarea
                  data-testid="contact-message"
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="A few sentences on what is not working, or what you are trying to launch."
                  className="rounded-none border border-white/15"
                />
                {errors.message && <div className="text-xs mt-1 font-semibold">{errors.message}</div>}
              </div>
              <div className="sm:col-span-2 flex items-start gap-3">
                <Checkbox
                  data-testid="contact-consent"
                  id="consent"
                  checked={form.consent}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, consent: !!v }))}
                  className="rounded-none border border-white/15"
                />
                <label htmlFor="consent" className="text-sm text-white/80">
                  I agree to be contacted by Kostaja about this inquiry. We do not share or sell your information.
                </label>
              </div>
              {errors.consent && (
                <div className="text-xs font-semibold sm:col-span-2 -mt-3">{errors.consent}</div>
              )}
              <div className="sm:col-span-2 mt-2">
                <PrimaryButton
                  type="submit"
                  testId="contact-submit"
                  onClick={() => {}}
                  className="w-full sm:w-auto"
                >
                  {submitting ? "Sending..." : "Send message"}
                </PrimaryButton>
                <p className="mt-4 text-xs text-white/50 max-w-md">
                  Your message goes directly to studio leadership. You will get a real reply, not a sales script.
                </p>
              </div>
            </form>
          </div>

          <aside className="lg:col-span-5">
            <div className="border border-white/15 p-6 lg:p-8">
              <Overline className="mb-3">Faster options</Overline>
              <h3 className="text-3xl lg:text-4xl font-black tracking-tighter leading-[0.95]">
                Three other doors in.
              </h3>
              <ul className="mt-6 space-y-4">
                <li>
                  <Link
                    to="/waitlist"
                    data-testid="aside-apply"
                    className="group flex items-center justify-between border-2 border-[#FFC600] bg-black p-4 hover:bg-[#FFC600] hover:text-black transition-colors"
                  >
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold">2027 project</div>
                      <div className="font-bold">Apply to Work With Kostaja</div>
                    </div>
                    <span>→</span>
                  </Link>
                </li>
                <li>
                  <a
                    href={SITE.emailHref}
                    data-testid="aside-email"
                    className="group flex items-center justify-between border border-white/15 p-4 hover:bg-black hover:text-[#FFC600] transition-colors"
                  >
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold">Email</div>
                      <div className="font-bold">{SITE.email}</div>
                    </div>
                    <span>→</span>
                  </a>
                </li>
                <li>
                  <a
                    href={SITE.phoneHref}
                    data-testid="aside-phone"
                    className="group flex items-center justify-between border border-white/15 p-4 hover:bg-black hover:text-[#FFC600] transition-colors"
                  >
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold">Phone</div>
                      <div className="font-bold">{SITE.phoneDisplay}</div>
                    </div>
                    <span>→</span>
                  </a>
                </li>
              </ul>
            </div>
            <div className="mt-6 border-2 border-[#FFC600] p-6 lg:p-8 bg-black text-white">
              <Overline color="yellow" className="mb-3">No mystery process</Overline>
              <p className="text-white/85 leading-relaxed">
                Reach out with what is not working. Scope, timeline, and price are written down before larger work begins.
              </p>
            </div>
          </aside>
        </div>
      </section>

      <CTABlock variant="contact" />
    </main>
  );
};

export default Contact;
